import React, { useEffect, useRef, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Alert, Box, Button, CircularProgress, Dialog, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Close, Download, Fullscreen, ZoomIn, ZoomOut } from '@mui/icons-material';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { tr } from 'helper/translate';

const DOCUMENT = gql`
  query PdfOfferImportDocument($id: String!) {
    document: pdfOfferImportDocument(id: $id)
  }
`;

function PdfPage({ document, pageNumber, width, zoom }: { document: PDFDocumentProxy; pageNumber: number; width: number; zoom: number }) {
  const canvasHolder = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  const [rendering, setRendering] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!width || !canvasHolder.current) return;
    let cancelled = false;
    let task: RenderTask | undefined;
    // Render off-screen and swap only the completed frame, so resizing never shows a blank page.
    const target = window.document.createElement('canvas');
    target.setAttribute('role', 'img');
    target.setAttribute('aria-label', tr('pdf-import-preview-page-label', { page: pageNumber, pages: document.numPages }));
    target.style.display = 'block';
    setRendering(true);
    setError('');
    void document
      .getPage(pageNumber)
      .then(async page => {
        if (cancelled) return;
        const original = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: (Math.min(Math.max(width - 32, 160), 1200) / original.width) * zoom });
        // Reserve the page's space before drawing, so the scroll position stays stable while pages render.
        setSize({ width: viewport.width, height: viewport.height });
        const ratio = Math.min(window.devicePixelRatio || 1, 2, Math.sqrt(16000000 / (viewport.width * viewport.height)));
        target.width = Math.floor(viewport.width * ratio);
        target.height = Math.floor(viewport.height * ratio);
        target.style.width = `${viewport.width}px`;
        target.style.height = `${viewport.height}px`;
        const context = target.getContext('2d');
        if (!context) throw new Error(tr('pdf-import-preview-draw-failed'));
        task = page.render({ canvas: target, canvasContext: context, viewport, transform: [ratio, 0, 0, ratio, 0, 0] });
        await task.promise;
        if (!cancelled) canvasHolder.current?.replaceChildren(target);
      })
      .catch(reason => {
        if (!cancelled) setError(reason instanceof Error ? reason.message : tr('pdf-import-preview-page-failed'));
      })
      .finally(() => {
        if (!cancelled) setRendering(false);
      });
    return () => {
      cancelled = true;
      task?.cancel();
    };
  }, [document, pageNumber, zoom, width]);
  return (
    <Box
      data-page={pageNumber}
      sx={{
        position: 'relative',
        width: size?.width,
        height: size?.height,
        minHeight: size ? undefined : 400,
        minWidth: size ? undefined : Math.max(width - 32, 160),
        bgcolor: 'white',
        boxShadow: '0 2px 12px #0002',
        flexShrink: 0,
      }}
    >
      {rendering && (
        <CircularProgress
          size={28}
          aria-label={tr('pdf-import-preview-page-drawing', { page: pageNumber })}
          sx={{ position: 'absolute', top: 16, left: '50%', zIndex: 1 }}
        />
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <div ref={canvasHolder} />
    </Box>
  );
}

/** All pages below each other in one scroll area, so no page is overlooked. */
function PdfPages({ document, zoom, onPageChange }: { document: PDFDocumentProxy; zoom: number; onPageChange: (page: number) => void }) {
  const container = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!container.current) return;
    let frame = 0;
    const observer = new ResizeObserver(([entry]) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setWidth(Math.floor(entry.contentRect.width)));
    });
    observer.observe(container.current);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, []);
  const updatePage = (): void => {
    const scroller = container.current;
    if (!scroller) return;
    // The current page is the last one whose top edge passed the upper third of the visible area.
    const line = scroller.getBoundingClientRect().top + scroller.clientHeight / 3;
    let current = 1;
    scroller.querySelectorAll<HTMLElement>('[data-page]').forEach(page => {
      if (page.getBoundingClientRect().top <= line) current = Number(page.dataset.page);
    });
    onPageChange(current);
  };
  return (
    <Box
      ref={container}
      onScroll={updatePage}
      sx={{ flex: 1, minHeight: 0, minWidth: 0, overflowX: 'auto', overflowY: 'scroll', bgcolor: 'grey.200' }}
    >
      <Stack spacing={2} alignItems="center" sx={{ boxSizing: 'border-box', minWidth: '100%', width: 'max-content', p: 2 }}>
        {Array.from({ length: document.numPages }, (_value, index) => (
          <PdfPage key={index} document={document} pageNumber={index + 1} width={width} zoom={zoom} />
        ))}
      </Stack>
    </Box>
  );
}

export function PdfPreview({ importId, filename }: { importId: string; filename: string }) {
  const [fullscreen, setFullscreen] = useState(false);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [error, setError] = useState('');
  const bytes = useRef<Uint8Array | null>(null);
  const [attempt, setAttempt] = useState(0);
  const {
    data,
    loading,
    error: loadError,
    refetch,
  } = useQuery<{ document: string }>(DOCUMENT, {
    variables: { id: importId },
    fetchPolicy: 'no-cache',
  });
  useEffect(() => {
    setPage(1);
    setZoom(1);
    setPdf(null);
    setError('');
    bytes.current = null;
    if (!data?.document) return;
    let cancelled = false;
    let destroy: (() => void) | undefined;
    void import('pdfjs-dist')
      .then(async pdfjs => {
        if (cancelled) return;
        pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();
        const binary = atob(data.document);
        const source = Uint8Array.from(binary, character => character.charCodeAt(0));
        bytes.current = source;
        const task = pdfjs.getDocument({ data: source.slice(), enableXfa: false });
        destroy = () => {
          void task.destroy();
        };
        const document = await task.promise;
        if (!cancelled) setPdf(document);
      })
      .catch(reason => {
        if (!cancelled) setError(reason instanceof Error ? reason.message : tr('pdf-import-preview-load-failed'));
      });
    return () => {
      cancelled = true;
      destroy?.();
      bytes.current = null;
    };
  }, [data?.document, attempt]);

  const download = (): void => {
    if (!bytes.current) return;
    const url = URL.createObjectURL(new Blob([bytes.current.buffer as ArrayBuffer], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const viewer = (
    <Stack sx={{ height: fullscreen ? '100dvh' : '75vh', minHeight: 320, bgcolor: 'background.paper' }}>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ p: 1, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" noWrap title={filename} sx={{ flex: 1, minWidth: 80, maxWidth: fullscreen ? undefined : 170 }}>
          {filename}
        </Typography>
        <Typography variant="body2" aria-live="polite" sx={{ px: 1, whiteSpace: 'nowrap' }} title={tr('pdf-import-preview-scroll-hint')}>
          {tr('pdf-import-preview-page', { page, pages: pdf?.numPages || '–' })}
        </Typography>
        <Tooltip title={tr('pdf-import-preview-zoom-out')}>
          <IconButton
            aria-label={tr('pdf-import-preview-zoom-out')}
            disabled={!pdf || zoom <= 0.5}
            onClick={() => setZoom(Math.max(0.5, zoom - 0.25))}
          >
            <ZoomOut />
          </IconButton>
        </Tooltip>
        <Button size="small" disabled={!pdf} onClick={() => setZoom(1)} title={tr('pdf-import-preview-fit-width')}>
          {Math.round(zoom * 100)} %
        </Button>
        <Tooltip title={tr('pdf-import-preview-zoom-in')}>
          <IconButton aria-label={tr('pdf-import-preview-zoom-in')} disabled={!pdf || zoom >= 3} onClick={() => setZoom(Math.min(3, zoom + 0.25))}>
            <ZoomIn />
          </IconButton>
        </Tooltip>
        <Tooltip title={tr('pdf-import-preview-download')}>
          <IconButton aria-label={tr('pdf-import-preview-download')} disabled={!bytes.current} onClick={download}>
            <Download />
          </IconButton>
        </Tooltip>
        {fullscreen ? (
          <Tooltip title={tr('pdf-import-preview-fullscreen-close-hint')}>
            <IconButton aria-label={tr('pdf-import-preview-fullscreen-close')} onClick={() => setFullscreen(false)} autoFocus>
              <Close />
            </IconButton>
          </Tooltip>
        ) : (
          <Button size="small" startIcon={<Fullscreen />} onClick={() => setFullscreen(true)}>
            {tr('pdf-import-preview-fullscreen')}
          </Button>
        )}
      </Stack>
      {error || loadError ? (
        <Alert
          severity="error"
          action={
            <Button
              onClick={() => {
                setAttempt(value => value + 1);
                void refetch();
              }}
            >
              {tr('pdf-import-preview-reload')}
            </Button>
          }
        >
          {error || loadError?.message}
        </Alert>
      ) : pdf ? (
        <PdfPages document={pdf} zoom={zoom} onPageChange={setPage} />
      ) : (
        <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
          <CircularProgress aria-label={tr(loading ? 'pdf-import-preview-loading' : 'pdf-import-preview-preparing')} />
        </Box>
      )}
    </Stack>
  );
  return (
    <>
      {fullscreen ? (
        <Dialog fullScreen open onClose={() => setFullscreen(false)} aria-label={tr('pdf-import-preview-fullscreen-label', { filename })}>
          {viewer}
        </Dialog>
      ) : (
        viewer
      )}
    </>
  );
}
